import {
    ChangeEvent,
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
    CRM_DROPDOWN_MAX_OPTIONS,
    CRM_OTHER_DETAILS_MAX_LENGTH,
} from '../../../common/config';
import { Dropdown } from '../../../components/Dropdown';
import { TemplateFlexGridContainer } from '../../../components/Grid';
import { Heading } from '../../../components/Heading';
import { Input } from '../../../components/Input';
import { InputGroup } from '../../../components/InputGroup';
import { RadioGroup } from '../../../components/RadioGroup';
import { Textarea } from '../../../components/Textarea';
import { DropdownOption } from '../../../lib/Dropdown/types';
import { noOp } from '../../../lib/utils';
import { PhRange, StepProps } from '../../../types';
import { useBriefingState } from '../BriefingState';
import { } from '../StepRestrictions';
import {
    onDropDownChange,
    onOtherChange,
} from '../StepRestrictions/StepRestrictions';

export const StepRequirements: FC<StepProps> = ({
  // mandatoryFields,
  onValidate = noOp,
}) => {
  const { t } = useTranslation();
  const [
    {
      requirementsMust,
      requirementsMustOther,
      requirementsCan,
      requirementsCanOther,
      requirementsApplicationConditionDetails,
      requirementsPHRange,
      requirementsAggressiveComponents,
      requirementsLightStability,
    },
    {
      setRequirementsMust,
      setRequirementsMustOther,
      setRequirementsCan,
      setRequirementsCanOther,
      setRequirementsApplicationConditionDetails,
      setRequirementsPHRange,
      setRequirementsAggressiveComponents,
      setRequirementsLightStability,
    },
  ] = useBriefingState();
  const dispatch = useDispatch();

  const [isRequirementsMustValid, setIsRequirementsMustValid] = useState(true);
  const [isRequirementsCanValid, setIsRequirementsCanValid] = useState(true);
  const isStepValid = useMemo(
    () => isRequirementsMustValid && isRequirementsCanValid,
    [isRequirementsCanValid, isRequirementsMustValid],
  );

  useEffect(() => onValidate(isStepValid), [isStepValid, onValidate]);

  const requirementsOptions = useMemo(
    () =>
      Object.entries(
        t('topics:requirements:options', { returnObjects: true }),
      ).map(([value, name]) => ({ name, value } as DropdownOption)),
    [t],
  );

  const requirementsMustOptions = useMemo(
    () =>
      requirementsOptions.filter(
        ({ value }) =>
          !requirementsCan?.some(
            (element: DropdownOption) => element.value === value,
          ) || value === 'other',
      ),
    [requirementsCan, requirementsOptions],
  );

  const requirementsCanOptions = useMemo(
    () =>
      requirementsOptions.filter(
        ({ value }) =>
          !requirementsMust?.some(
            (element: DropdownOption) => element.value === value,
          ) || value === 'other',
      ),
    [requirementsMust, requirementsOptions],
  );

  const onPhChange = useCallback(
    (
      type: keyof PhRange,
      { target: { value } }: ChangeEvent<HTMLInputElement>,
    ) => {
      if (value && isNaN(parseFloat(value))) return;

      let truthyValue: number | null;
      if (!value.length) {
        truthyValue = null;
      }
      truthyValue = parseFloat(value);

      dispatch(
        setRequirementsPHRange({
          ...requirementsPHRange,
          [type]: truthyValue || null,
        }),
      );
    },
    [dispatch, requirementsPHRange, setRequirementsPHRange],
  );

  return (
    <TemplateFlexGridContainer.BriefingStep>
      <Heading
        level="h1"
        margin={0}
        text={t('topics:requirements:heading:general')}
        optionalField={false}
      />
      <TemplateFlexGridContainer.BriefingStepBody>
        <TemplateFlexGridContainer.BriefingStepSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="w-full md:w-10/12">
                <InputGroup
                  label={t('topics:requirements:mustComply')}
                  errorMessage={
                    requirementsOptions
                      ? ''
                      : `${t(
                          'topics:formulation-attributes:errors:productMarketingClaims',
                        )}`
                  }
                  optional={true}
                >
                  <Dropdown
                    initialSelection={requirementsMust || []}
                    options={requirementsMustOptions || []}
                    error={!requirementsMustOptions}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(selectedOption) => {
                      onDropDownChange(
                        dispatch,
                        selectedOption,
                        setRequirementsMust,
                        setRequirementsMustOther,
                      );
                    }}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>
              <div className="w-full md:w-10/12">
                {(requirementsMust || []).some(
                  (option) => option && option.value === 'other',
                ) && (
                  <InputGroup
                    label={t('topics:requirements:specifyRequirements')}
                  >
                    <div className="w-full __specification__ h-32">
                      <Textarea
                        placeholder={t('placeholders:textareaDefault')}
                        initialValue={requirementsMustOther || undefined}
                        onChange={(e: ChangeEvent) =>
                          onOtherChange(dispatch, e, setRequirementsMustOther)
                        }
                        onValidate={setIsRequirementsMustValid}
                        maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                      />
                    </div>
                  </InputGroup>
                )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
            <TemplateFlexGridContainer.BriefingStepSection>
              <div className="w-full md:w-10/12">
                <InputGroup
                  label={t('topics:requirements:canComply')}
                  errorMessage={
                    requirementsOptions
                      ? ''
                      : `${t(
                          'topics:formulation-attributes:errors:productMarketingClaims',
                        )}`
                  }
                  optional={true}
                >
                  <Dropdown
                    initialSelection={requirementsCan || []}
                    options={requirementsCanOptions || []}
                    error={!requirementsCanOptions}
                    multiselect={true}
                    maxCount={CRM_DROPDOWN_MAX_OPTIONS}
                    onChange={(selectedOption) => {
                      onDropDownChange(
                        dispatch,
                        selectedOption,
                        setRequirementsCan,
                        setRequirementsCanOther,
                      );
                    }}
                  />
                  <p className="mt-1 text-gray-600 text-2xs font-regular">
                    {t('labels:multiSelectHint')}
                  </p>
                </InputGroup>
              </div>
              <div className="w-full md:w-10/12">
                {(requirementsCan || []).some(
                  (option) => option && option.value === 'other',
                ) && (
                  <InputGroup
                    label={t('topics:requirements:specifyRequirements')}
                  >
                    <div className="w-full __specification__ h-32">
                      <Textarea
                        placeholder={t('placeholders:textareaDefault')}
                        initialValue={requirementsCanOther || undefined}
                        onChange={(e: ChangeEvent) =>
                          onOtherChange(dispatch, e, setRequirementsCanOther)
                        }
                        onValidate={setIsRequirementsCanValid}
                        maxLength={CRM_OTHER_DETAILS_MAX_LENGTH}
                      />
                    </div>
                  </InputGroup>
                )}
              </div>
            </TemplateFlexGridContainer.BriefingStepSection>
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>

        <TemplateFlexGridContainer.BriefingStepSection spaceY={8}>
          <Heading
            level="h3"
            margin={0}
            text={t('topics:requirements:heading:specify')}
          />
          <div className="w-full md:w-8/12">
            <InputGroup
              label={t(
                'topics:requirements:applicationConditionDetails:heading',
              )}
              optional={true}
            >
              <Input
                initialValue={requirementsApplicationConditionDetails}
                type="text"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setRequirementsApplicationConditionDetails(
                      e.target.value,
                    ),
                  );
                }}
              />
            </InputGroup>
          </div>

          <div className="w-full md:w-8/12">
            <InputGroup
              label={t('topics:requirements:pHRange:heading')}
              optional={true}
            >
              <div className="flex font-bold xs:items-baseline md:items-center xs:space-x-0 md:space-x-5 xs:flex-col md:flex-row text-gray-700">
                <div className="flex items-center space-x-1 xs:mb-5 md:mb-0">
                  <div className="relative flex max-w-[70px]" data-testid="ph-range-min">
                    <Input
                      initialValue={requirementsPHRange.min}
                      type="number"
                      placeholder="0"
                      onChange={onPhChange.bind(null, 'min')}
                    />
                  </div>
                  <div>-</div>
                  <div className="flex max-w-[70px]" data-testid="ph-range-max">
                    <Input
                      initialValue={requirementsPHRange.max}
                      type="number"
                      placeholder="14"
                      onChange={onPhChange.bind(null, 'max')}
                    />
                  </div>
                </div>
              </div>
            </InputGroup>
          </div>

          <div className="w-full md:w-8/12">
            <InputGroup
              label={t('topics:requirements:aggressiveComponents:heading')}
            >
              <Input
                initialValue={requirementsAggressiveComponents}
                type="text"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  dispatch(
                    setRequirementsAggressiveComponents(e.target.value),
                  );
                }}
              />
            </InputGroup>
          </div>

          <div className="w-full md:w-8/12">
            <RadioGroup
              label={t('topics:requirements:lightStability:label')}
              onChange={(selectedValue: string | null) => {
                dispatch(setRequirementsLightStability(selectedValue));
              }}
              initialSelection={requirementsLightStability}
              radioOptions={[
                {
                  label: t('topics:requirements:lightStability:answer:yes'),
                  value: t('topics:requirements:lightStability:answer:yes'),
                },
                {
                  label: t('topics:requirements:lightStability:answer:no'),
                  value: t('topics:requirements:lightStability:answer:no'),
                },
              ]}
            />
          </div>
        </TemplateFlexGridContainer.BriefingStepSection>
      </TemplateFlexGridContainer.BriefingStepBody>
    </TemplateFlexGridContainer.BriefingStep>
  );
};
